import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Form, Row, Col, Button, Alert } from 'react-bootstrap';
import * as actionCreator from '../../actions/';
import API from '../../api';
import {
    getCurrentVAccount,
    getUserData,
    syncDrivePermissionsForEntity,
} from '../../helpers/Session';
import AppOverlay from '../../components/Overlay/Overlay';
import LoginBackgroundURL from '../../assets/img/bg-login.jpeg';
import AppConfig from '../../config/default';
import languageManager from '../../helpers/Language';
import './Login.scss';
import { getPortalModules, getTagnameId } from '../../api/actions/Session';

export const MIN_TIME_ANIMATION = 0;
export const MIN_TIME_WITH_SESSION = 0;
export const BrandLogoURL = AppConfig.APP_MENU_ICON_URL;
export const BrandLogoConf = AppConfig.LOGIN_BRAND_LOGO;

const Login = ({ session, setSession, setProfile }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [formDisabled, setFormDisabled] = useState(false);
    const [otpModeEnter, setOtpModeEnter] = useState(false);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        otp: '',
    });
    let from = location.state?.from?.pathname || '/';

    useEffect(() => {}, [formDisabled]);

    const onSubmit = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        setFormDisabled(true);
        const { username, password, otp } = formData;
        const sanitizedUsername = username.toLowerCase().trim();

        try {
            const res = await API.Session.auth(
                sanitizedUsername,
                password,
                otp
            );
            if (res && res.access_token) {
                setFormData({
                    username: '',
                    password: '',
                    otp: '',
                });
                setSession(res);
                const profile = await getUserData();
                const tagsId = await getTagnameId();

                const currentVA = getCurrentVAccount(profile, session);

                // set permissions from vAccounts selected.
                profile._permissions = currentVA?.user?.permissions;
                setProfile(profile);
                setSession({
                    ...res,
                    currentVirtualAccount: currentVA?.entityId,
                    tagsId,
                });

                const modules = await getPortalModules(currentVA.entityId);

                setSession({
                    ...res,
                    currentVirtualAccount: currentVA?.entityId,
                    modules,
                    tagsId,
                });

                // syncDrivePermissions with selected VA.
                await syncDrivePermissionsForEntity();
                navigate(from, { replace: true });
            }
        } catch (e) {
            console.log(e);

            if (e.response) {
                const response = e.response;
                const data = response.data || {};

                if (data.error === 'mfa-alert-exception') {
                    if (data.information === 'otp-code-missing') {
                        setOtpModeEnter(true);
                    }
                } else {
                    const newError = {
                        code: data?.code || 500,
                        error: data?.error || 'unknown',
                        information:
                            data?.information || 'Something went wrong',
                    };

                    if (
                        data?.message &&
                        data.message === 'permission-not-found'
                    ) {
                        newError.error = data.message;
                        newError.information = languageManager.T(
                            'LOGIN_FAILED_WAITING_PERMISSION'
                        );
                    }

                    setError(newError);
                }
            }
            setFormDisabled(false);
        }
    };

    const onChange = (event) => {
        const { name, value } = event.target;
        setFormData({ ...formData, [name]: value });
    };

    return (
        <div
            className="login"
            style={{ backgroundImage: `url(${LoginBackgroundURL})` }}
        >
            <div className="auth-box shadow p-4 pb-5 pt-5 mb-5rounded bg-blur-effect rounded">
                <Form autoComplete="chrome-off" onSubmit={onSubmit}>
                    <Row className="flex-column">
                        <Col className="text-center">
                            <img
                                {...BrandLogoConf}
                                className="mb-4"
                                src={BrandLogoURL}
                                alt="Brand logo"
                            />
                        </Col>
                        <Col className={otpModeEnter ? 'd-none' : ''}>
                            <Form.Group className="mb-4">
                                <Form.Label className="fw-bold">
                                    Email Address
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="username"
                                    placeholder="your_email@address.com"
                                    disabled={formDisabled}
                                    onChange={onChange}
                                    value={formData.username}
                                />
                            </Form.Group>
                        </Col>
                        <Col className={otpModeEnter ? 'd-none' : ''}>
                            <Form.Group className={error ? 'mb-3' : 'mb-5'}>
                                <Form.Label className="fw-bold">
                                    Password
                                </Form.Label>
                                <Form.Control
                                    type="password"
                                    name="password"
                                    placeholder="*********"
                                    disabled={formDisabled}
                                    onChange={onChange}
                                    value={formData.password}
                                />
                            </Form.Group>
                        </Col>
                        <Col className={otpModeEnter ? '' : 'd-none'}>
                            <Form.Group className="mb-5">
                                <Form.Label className="fw-bold">
                                    OTP Code
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="otp"
                                    placeholder="*********"
                                    disabled={formDisabled}
                                    onChange={onChange}
                                />
                            </Form.Group>
                        </Col>
                        {error && (
                            <Col>
                                <Alert
                                    variant={
                                        error?.error === 'permission-not-found'
                                            ? 'info'
                                            : 'danger'
                                    }
                                    onClose={() => setError(false)}
                                    dismissible
                                >
                                    {error.information}
                                </Alert>
                            </Col>
                        )}
                        <Col>
                            <Button
                                type="submit"
                                className="full-width mb-2"
                                disabled={formDisabled}
                            >
                                Sign in
                            </Button>
                            <Link
                                className="full-width btn btn-link mt-3"
                                role="button"
                                disabled={formDisabled}
                                to="/forgot-password"
                            >
                                forgot my password
                            </Link>
                        </Col>
                    </Row>
                </Form>
            </div>
            <AppOverlay show={formDisabled} />
        </div>
    );
};

const mapStateToProps = (appState) => ({
    session: appState.session,
    preferences: appState.preferences,
});

const mapDispatchToProps = (dispatch) => ({
    setSession: (session) =>
        dispatch(actionCreator.Session.setSession(session)),
    setProfile: (profile) =>
        dispatch(actionCreator.Profile.setProfile(profile)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
